import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Mic, Mail, RefreshCw, ExternalLink, Trash2, Radio, Search, Play,
  Eye, Upload, Users, UserPlus, Inbox, Clock, CheckCircle, FileText,
  Save, X, Calendar, Sparkles, Copy, History
} from 'lucide-react';
import { API_URL } from '../../config/api';
import { parseTalksResponse, fetchLiveRooms, fetchLiveHistory } from '../../services/qoreTalksApi';
import './QoreTalksAdmin.css';

const QTHeader = ({ icon: Icon, title, subtitle, children }) => (
  <div className="qt-admin__header">
    <div>
      <h2><Icon size={22} /> {title}</h2>
      <p>{subtitle}</p>
    </div>
    {children && <div className="qt-admin__actions">{children}</div>}
  </div>
);

const QTStats = ({ items }) => (
  <div className="qt-stats">
    {items.map((s) => (
      <div key={s.label} className="qt-stat">
        <div className="qt-stat__icon" style={{ background: s.color }}>{s.icon}</div>
        <div>
          <p className="qt-stat__value">{s.value}</p>
          <p className="qt-stat__label">{s.label}</p>
        </div>
      </div>
    ))}
  </div>
);

const QTEmpty = ({ icon: Icon, title, desc, action }) => (
  <div className="qt-empty">
    <Icon size={48} />
    <h4>{title}</h4>
    <p>{desc}</p>
    {action}
  </div>
);

const statusClass = (s) => `qt-status qt-status--${s || 'draft'}`;

export const QoreTalksEpisodesPanel = ({ token }) => {
  const [talks, setTalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/qoretalks?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTalks(res.ok ? parseTalksResponse(await res.json()) : []);
    } catch {
      setTalks([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const filtered = useMemo(() => {
    return talks.filter((t) => {
      const matchStatus = statusFilter === 'all' || t.status === statusFilter;
      const q = search.toLowerCase();
      const matchSearch = !q || t.title?.toLowerCase().includes(q) || t.category?.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [talks, statusFilter, search]);

  const stats = useMemo(() => ({
    total: talks.length,
    published: talks.filter((t) => t.status === 'published').length,
    draft: talks.filter((t) => t.status === 'draft').length,
    views: talks.reduce((s, t) => s + (t.analytics?.views || 0), 0),
  }), [talks]);

  const publish = async (id) => {
    await fetch(`${API_URL}/qoretalks/${id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'published' }),
    });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this episode permanently?')) return;
    await fetch(`${API_URL}/qoretalks/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  return (
    <div className="qt-admin tab-qoretalks">
      <QTHeader
        icon={Mic}
        title="Episodes"
        subtitle="Publish podcast episodes, manage drafts, and track performance."
      >
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
        <Link to="/qoretalks/manage" className="qt-btn qt-btn--primary"><Upload size={15} /> Upload</Link>
        <Link to="/qoretalks/live?create=true" className="qt-btn qt-btn--rose"><Radio size={15} /> Go Live</Link>
      </QTHeader>

      <QTStats items={[
        { icon: <Mic size={18} />, value: stats.total, label: 'Total Episodes', color: '#6366f1' },
        { icon: <Play size={18} />, value: stats.published, label: 'Published', color: '#10b981' },
        { icon: <FileText size={18} />, value: stats.draft, label: 'Drafts', color: '#64748b' },
        { icon: <Eye size={18} />, value: stats.views, label: 'Total Views', color: '#f59e0b' },
      ]} />

      <div className="qt-toolbar">
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.45 }} />
          <input className="qt-search" style={{ width: '100%' }} placeholder="Search episodes…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="qt-filter-pills">
          {['all', 'published', 'draft', 'scheduled'].map((s) => (
            <button key={s} type="button" className={`qt-pill ${statusFilter === s ? 'qt-pill--active' : ''}`} onClick={() => setStatusFilter(s)}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading-state"><RefreshCw className="spin" size={24} /><p>Loading episodes…</p></div>
      ) : filtered.length === 0 ? (
        <QTEmpty
          icon={Mic}
          title="No episodes yet"
          desc="Upload your first QoreTalks episode to go live on the public hub."
          action={<Link to="/qoretalks/manage" className="qt-btn qt-btn--primary"><Upload size={15} /> Upload Episode</Link>}
        />
      ) : (
        <div className="qt-episodes-grid">
          {filtered.map((t) => (
            <article key={t._id} className="qt-episode-card">
              <div className="qt-episode-card__thumb">
                <Mic size={32} style={{ opacity: 0.35, color: '#a5b4fc' }} />
                <span className={statusClass(t.status)} style={{ position: 'absolute', top: 8, right: 8 }}>{t.status}</span>
              </div>
              <div className="qt-episode-card__body">
                <h3 className="qt-episode-card__title">{t.title}</h3>
                <p className="qt-episode-card__meta">
                  {t.category} · {t.duration || '—'} min · {t.analytics?.views || 0} views
                  {t.season?.episode ? ` · S${t.season.number || 1}E${t.season.episode}` : ''}
                </p>
                <div className="qt-episode-card__footer">
                  <Link to={`/qoretalks/episodes/${t._id}`} className="qt-btn qt-btn--ghost qt-btn--sm" target="_blank" rel="noreferrer"><ExternalLink size={12} /> View</Link>
                  {t.status !== 'published' && (
                    <button type="button" className="qt-btn qt-btn--primary qt-btn--sm" onClick={() => publish(t._id)}>Publish</button>
                  )}
                  <button type="button" className="qt-btn qt-btn--danger qt-btn--sm" onClick={() => remove(t._id)}><Trash2 size={12} /></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export const QoreTalksSubscribersPanel = ({ token }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/qoretalks/subscribers?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = res.ok ? await res.json() : {};
      setSubscribers(json.data || []);
    } catch {
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const epCount = subscribers.filter((s) => s.preferences?.episodes !== false).length;
  const liveCount = subscribers.filter((s) => s.preferences?.launch !== false).length;

  return (
    <div className="qt-admin tab-qoretalks">
      <QTHeader icon={Mail} title="Subscribers" subtitle="Audience members who signed up for episode and live AMA alerts.">
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
        <Link to="/qoretalks/subscribe" className="qt-btn qt-btn--ghost" target="_blank" rel="noreferrer"><ExternalLink size={15} /> Public Page</Link>
      </QTHeader>

      <QTStats items={[
        { icon: <Users size={18} />, value: subscribers.length, label: 'Total Subscribers', color: '#6366f1' },
        { icon: <Mic size={18} />, value: epCount, label: 'Episode Alerts', color: '#10b981' },
        { icon: <Radio size={18} />, value: liveCount, label: 'Live Alerts', color: '#f43f5e' },
      ]} />

      {loading ? (
        <div className="loading-state"><RefreshCw className="spin" size={24} /></div>
      ) : subscribers.length === 0 ? (
        <QTEmpty
          icon={Mail}
          title="No subscribers yet"
          desc="Share the subscribe page to grow your QoreTalks audience."
          action={<Link to="/qoretalks/subscribe" className="qt-btn qt-btn--primary" target="_blank" rel="noreferrer">Open Subscribe Page</Link>}
        />
      ) : (
        <div className="qt-subscribers-table">
          {subscribers.map((s) => (
            <div key={s._id} className="qt-sub-row">
              <div className="qt-sub-avatar">{(s.email || '?').charAt(0).toUpperCase()}</div>
              <div>
                <p className="qt-sub-email">{s.email}</p>
                <p className="qt-sub-date">Joined {new Date(s.subscribedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}</p>
              </div>
              <div className="qt-chips">
                <span className={s.preferences?.episodes !== false ? 'qt-chip' : 'qt-chip qt-chip--off'}>Episodes</span>
                <span className={s.preferences?.launch !== false ? 'qt-chip' : 'qt-chip qt-chip--off'}>Live</span>
                <span className={s.preferences?.exclusive !== false ? 'qt-chip' : 'qt-chip qt-chip--off'}>Exclusive</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatDuration = (seconds = 0) => {
  const s = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
};

export const QoreTalksLivestreamPanel = ({ token, onNotify }) => {
  const [liveRooms, setLiveRooms] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rooms, hist] = await Promise.all([
        fetchLiveRooms().catch(() => []),
        token ? fetchLiveHistory(token, { limit: 30 }).catch(() => ({ sessions: [] })) : { sessions: [] },
      ]);
      setLiveRooms(rooms);
      setHistory(hist.sessions || []);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const copyInvite = async (roomId) => {
    const url = `${window.location.origin}/qoretalks/live/${roomId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(roomId);
      onNotify?.('success', 'Invite copied', url);
      setTimeout(() => setCopied(''), 1600);
    } catch {
      onNotify?.('error', 'Copy failed', url);
    }
  };

  return (
    <div className="qt-admin tab-qoretalks">
      <QTHeader icon={Radio} title="Livestream" subtitle="Zoom-style meetings with a shareable link. Guests join from the invite; ended calls land in history.">
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
        <Link to="/qoretalks/live?create=true" className="qt-btn qt-btn--rose"><Radio size={15} /> New meeting</Link>
      </QTHeader>

      <QTStats items={[
        { icon: <Radio size={18} />, value: liveRooms.length, label: 'Open now', color: '#f43f5e' },
        { icon: <History size={18} />, value: history.length, label: 'Recent calls', color: '#6366f1' },
        { icon: <Users size={18} />, value: history.reduce((s, c) => s + (c.attendees?.length || 0), 0), label: 'Attendees logged', color: '#10b981' },
      ]} />

      <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Live & waiting</h3>
      {loading && liveRooms.length === 0 ? (
        <div className="loading-state"><RefreshCw className="spin" size={22} /></div>
      ) : liveRooms.length === 0 ? (
        <QTEmpty
          icon={Radio}
          title="No open meetings"
          desc="Create a meeting, copy the invite link, and share it like a Zoom call."
          action={<Link to="/qoretalks/live?create=true" className="qt-btn qt-btn--rose">Start meeting</Link>}
        />
      ) : (
        <div className="qt-episodes-grid">
          {liveRooms.map((room) => (
            <div key={room.roomId || room._id} className="qt-episode-card">
              <div className="qt-episode-card__thumb" style={{ background: 'linear-gradient(135deg, #7f1d1d, #1e1b4b)' }}>
                <span className="qt-status qt-status--published" style={{ position: 'absolute', top: 8, left: 8 }}>
                  {room.isLive ? 'LIVE' : 'WAITING'}
                </span>
                <Radio size={28} style={{ color: '#f87171' }} />
              </div>
              <div className="qt-episode-card__body">
                <h3 className="qt-episode-card__title">{room.title}</h3>
                <p className="qt-episode-card__meta">
                  {room.roomId} · {room.host?.name || 'Host'} · {room.participantCount || 0} in room
                </p>
                <div className="qt-episode-card__footer" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Link to={`/qoretalks/live/${room.roomId || room._id}?host=1`} className="qt-btn qt-btn--rose qt-btn--sm">Join as host</Link>
                  <button type="button" className="qt-btn qt-btn--ghost qt-btn--sm" onClick={() => copyInvite(room.roomId || room._id)}>
                    <Copy size={13} /> {copied === (room.roomId || room._id) ? 'Copied' : 'Copy invite'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <h3 style={{ margin: '2rem 0 0.75rem', fontSize: '1rem' }}><History size={16} style={{ verticalAlign: -2 }} /> Call history</h3>
      {history.length === 0 ? (
        <p className="empty-state">Ended meetings will show duration, host, and who joined.</p>
      ) : (
        <div className="qt-subscribers-table">
          {history.map((call) => (
            <div key={call.id || call.roomId} className="qt-sub-row" style={{ alignItems: 'flex-start' }}>
              <div className="qt-sub-avatar">{(call.title || 'C').charAt(0)}</div>
              <div style={{ flex: 1 }}>
                <p className="qt-sub-email">{call.title}</p>
                <p className="qt-sub-date">
                  {call.roomId} · {call.status} · {call.host?.name || 'Host'} · {formatDuration(call.durationSeconds)}
                  {call.startedAt ? ` · ${new Date(call.startedAt).toLocaleString()}` : ` · ${new Date(call.createdAt).toLocaleString()}`}
                </p>
                {call.attendees?.length > 0 && (
                  <p className="qt-sub-date">
                    {call.attendees.length} attendee{call.attendees.length === 1 ? '' : 's'}: {call.attendees.map((a) => a.name).filter(Boolean).slice(0, 8).join(', ')}
                  </p>
                )}
              </div>
              <span className={`qt-status qt-status--${call.status === 'ended' ? 'draft' : 'published'}`}>{call.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const QoreTalksGuestsPanel = ({
  token,
  showNotification,
  setConfirmModal,
}) => {
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/qoretalks/guest-applications?status=${status}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setApplications(data.data || []);
        setStats(data.stats || {});
      }
    } catch {
      showNotification?.('error', 'Fetch Failed', 'Could not load guest applications.');
    } finally {
      setLoading(false);
    }
  }, [token, status, showNotification]);

  useEffect(() => { load(); }, [load]);

  const updateApp = async (id, newStatus, newNotes) => {
    try {
      const res = await fetch(`${API_URL}/qoretalks/guest-applications/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, notes: newNotes }),
      });
      const data = await res.json();
      if (data.success) {
        showNotification?.('success', 'Updated', `Status set to ${newStatus}.`);
        load();
        setSelected(null);
      }
    } catch {
      showNotification?.('error', 'Error', 'Update failed.');
    }
  };

  const deleteApp = (id) => {
    const run = async () => {
      const res = await fetch(`${API_URL}/qoretalks/guest-applications/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        showNotification?.('success', 'Deleted', 'Application removed.');
        load();
        setSelected(null);
      }
    };
    if (setConfirmModal) {
      setConfirmModal({
        isOpen: true,
        title: 'Delete application',
        message: 'This cannot be undone.',
        danger: true,
        onConfirm: () => { setConfirmModal({ isOpen: false }); run(); },
      });
    } else if (window.confirm('Delete this application?')) {
      run();
    }
  };

  return (
    <div className="qt-admin tab-qoretalks">
      <QTHeader icon={UserPlus} title="Guest Applications" subtitle="Review speaker pitches from /qoretalks/apply and move candidates through your pipeline.">
        <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="contacted">Contacted</option>
        </select>
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
        <Link to="/qoretalks/apply" className="qt-btn qt-btn--ghost" target="_blank" rel="noreferrer"><ExternalLink size={15} /> Apply Page</Link>
      </QTHeader>

      {stats && (
        <QTStats items={[
          { icon: <Inbox size={18} />, value: stats.total || 0, label: 'Total', color: '#3b82f6' },
          { icon: <Clock size={18} />, value: stats.pending || 0, label: 'Pending', color: '#f59e0b' },
          { icon: <CheckCircle size={18} />, value: stats.accepted || 0, label: 'Accepted', color: '#10b981' },
          { icon: <Mail size={18} />, value: stats.contacted || 0, label: 'Contacted', color: '#8b5cf6' },
        ]} />
      )}

      <div className="qt-guests-split">
        <div className="list-panel" style={{ borderRadius: 'var(--admin-radius)', overflow: 'hidden' }}>
          <div className="list-header" style={{ padding: '1rem 1.1rem', borderBottom: '1px solid var(--admin-border)' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Applications</h3>
            <span className="count-badge">{applications.length}</span>
          </div>
          {loading ? (
            <div className="loading-state" style={{ padding: '2rem' }}><RefreshCw className="spin" size={22} /></div>
          ) : applications.length === 0 ? (
            <QTEmpty icon={UserPlus} title="No applications" desc="Guest pitches will appear when someone submits the apply form." />
          ) : (
            applications.map((app) => (
              <div
                key={app._id}
                className={`qt-guest-item ${selected?._id === app._id ? 'active' : ''}`}
                onClick={() => { setSelected(app); setNotes(app.notes || ''); }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <h4>{app.name || app.email}</h4>
                  <span className={statusClass(app.status)}>{app.status}</span>
                </div>
                {app.topic && <p className="qt-guest-topic">{app.topic}</p>}
                <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--admin-text-secondary)', lineHeight: 1.4 }}>
                  {app.message?.slice(0, 90)}{(app.message?.length > 90) ? '…' : ''}
                </p>
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.7rem', color: 'var(--admin-text-secondary)' }}>
                  <Calendar size={11} style={{ verticalAlign: -2, marginRight: 4 }} />
                  {new Date(app.submittedAt).toLocaleDateString()}
                </p>
              </div>
            ))
          )}
        </div>

        {selected ? (
          <div className="qt-detail-card">
            <div className="qt-detail-card__head">
              <div>
                <h3 style={{ margin: '0 0 0.35rem', fontSize: '1.1rem' }}>{selected.name || 'Guest applicant'}</h3>
                <span className={statusClass(selected.status)}>{selected.status}</span>
              </div>
              <button type="button" className="btn-close-detail" onClick={() => setSelected(null)} aria-label="Close"><X size={18} /></button>
            </div>

            <div className="qt-detail-section">
              <h4><Mail size={14} /> Contact</h4>
              <div className="info-row"><span className="label">Email</span><a href={`mailto:${selected.email}`} className="value">{selected.email}</a></div>
              {selected.linkedinUrl && (
                <div className="info-row"><span className="label">LinkedIn</span><a href={selected.linkedinUrl} className="value" target="_blank" rel="noreferrer">{selected.linkedinUrl}</a></div>
              )}
              {selected.expertiseArea && (
                <div className="info-row"><span className="label">Expertise</span><span className="value">{selected.expertiseArea}</span></div>
              )}
              {selected.topic && (
                <div className="info-row"><span className="label">Topic</span><span className="value">{selected.topic}</span></div>
              )}
              <div className="info-row"><span className="label">Submitted</span><span className="value">{new Date(selected.submittedAt).toLocaleString()}</span></div>
            </div>

            <div className="qt-detail-section">
              <h4><FileText size={14} /> Message</h4>
              <div className="message-content">{selected.message}</div>
            </div>

            <div className="qt-detail-section">
              <h4><FileText size={14} /> Admin notes</h4>
              <textarea className="notes-textarea" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes…" />
            </div>

            <div className="qt-detail-actions">
              <select className="status-select" value={selected.status} onChange={(e) => updateApp(selected._id, e.target.value, notes)}>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="contacted">Contacted</option>
              </select>
              <button type="button" className="qt-btn qt-btn--primary qt-btn--sm" onClick={() => updateApp(selected._id, selected.status, notes)}><Save size={14} /> Save</button>
              <button type="button" className="qt-btn qt-btn--danger qt-btn--sm" onClick={() => deleteApp(selected._id)}><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        ) : (
          <div className="qt-empty" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Sparkles size={40} />
            <h4>Select an application</h4>
            <p>Choose a guest pitch from the list to review details and update status.</p>
          </div>
        )}
      </div>
    </div>
  );
};